import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bell, CalendarClock, ImagePlus, Send, X } from 'lucide-react';
import { cn } from '@lib/utils';
import { opsService, type NotificationTarget } from '@services/ops.service';
import { usersService } from '@services/users.service';

/**
 * Compose and send notifications: one user, several users, everyone on one
 * app, or everyone. Optional image (uploaded to S3 notifications/ via presign,
 * public CloudFront URL) renders in the in-app feed AND expands in the
 * Android/iOS notification tray.
 *
 * Layout: a sectioned composer (Audience / Message / Attachment / Delivery,
 * each a label-column + control-column row) beside a "Recent sends" rail.
 */

const statusChip: Record<string, string> = {
  pending: 'bg-[var(--state-warning-bg)] text-[var(--state-warning)]',
  failed: 'bg-[var(--state-danger-bg)] text-[var(--state-danger)]',
  cancelled: 'bg-[var(--bg-base)] text-[var(--text-muted)]',
  sending: 'bg-[var(--state-success-bg)] text-[var(--state-success)]',
};

/** One labelled section row of the composer: uppercase kicker + helper on the
 *  left, controls on the right, so the form breathes across the full width. */
function ComposerSection({
  title,
  hint,
  children,
}: {
  title: string;
  hint: string;
  children: React.ReactNode;
}) {
  return (
    <div className="px-6 py-6 grid gap-x-10 gap-y-3 lg:grid-cols-[190px_minmax(0,1fr)]">
      <div>
        <p className="text-[12px] font-semibold uppercase tracking-[0.08em] text-[var(--text-primary)]">
          {title}
        </p>
        <p className="text-[12px] text-[var(--text-muted)] leading-relaxed mt-1">{hint}</p>
      </div>
      <div className="min-w-0">{children}</div>
    </div>
  );
}

export const NotificationsSendPage = () => {
  const queryClient = useQueryClient();
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [audience, setAudience] = useState<'users' | 'app' | 'all'>('app');
  const [app, setApp] = useState<'customer' | 'merchant' | 'rider'>('customer');
  const [userQuery, setUserQuery] = useState('');
  const [selectedUsers, setSelectedUsers] = useState<{ userId: string; label: string }[]>([]);
  const [result, setResult] = useState<string | null>(null);
  const [scheduledAt, setScheduledAt] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);

  const { data: allUsers = [] } = useQuery({
    queryKey: ['admin-users'],
    queryFn: () => usersService.list({ size: 200 }),
  });
  const { data: history = [] } = useQuery({
    queryKey: ['admin-notifications'],
    queryFn: opsService.notificationHistory,
  });

  const matches = userQuery.trim().length < 2 ? [] : allUsers
    .filter((u: any) => {
      const hay = `${u.firstName ?? ''} ${u.lastName ?? ''} ${u.phone ?? ''} ${u.email ?? ''}`.toLowerCase();
      return hay.includes(userQuery.toLowerCase()) &&
        !selectedUsers.some((s) => s.userId === u.userId);
    })
    .slice(0, 6);

  const send = useMutation({
    mutationFn: () => {
      const target: NotificationTarget =
        audience === 'all' ? { type: 'all' }
        : audience === 'app' ? { type: 'app', app }
        : { type: 'users', userIds: selectedUsers.map((u) => u.userId) };
      return opsService.sendNotification({
        title, body, imageUrl: imageUrl ?? undefined,
        scheduledAt: scheduledAt ? new Date(scheduledAt).toISOString() : undefined,
        target,
      });
    },
    onSuccess: (r) => {
      setResult(scheduledAt
        ? `Scheduled for ${new Date(scheduledAt).toLocaleString()} (~${r.recipients} recipients).`
        : `Sent to ${r.recipients} recipient${r.recipients === 1 ? '' : 's'}.`);
      setTitle(''); setBody(''); setImageUrl(null); setSelectedUsers([]); setScheduledAt('');
      queryClient.invalidateQueries({ queryKey: ['admin-notifications'] });
    },
    onError: (e: any) => setResult(e?.message ?? 'Failed to send.'),
  });

  const cancelMutation = useMutation({
    mutationFn: (id: string) => opsService.cancelNotification(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin-notifications'] }),
  });

  const pickImage = async (file: File) => {
    setUploading(true);
    try {
      setImageUrl(await opsService.uploadNotificationImage(file));
    } catch {
      setResult('Image upload failed.');
    } finally {
      setUploading(false);
    }
  };

  const canSend = title.trim() && body.trim() && !uploading &&
    (audience !== 'users' || selectedUsers.length > 0);

  const inputCls =
    'w-full rounded-2xl border border-[var(--border-default)] bg-white px-4 py-2.5 text-[14px] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none focus:border-[var(--border-strong)] transition-colors duration-200';
  const chip = (active: boolean) =>
    cn(
      'px-4 py-2 rounded-full text-[13px] font-semibold transition-all duration-200',
      active
        ? 'bg-[var(--brand-forest)] text-white'
        : 'border border-[var(--border-subtle)] text-[var(--text-secondary)] hover:border-[var(--border-default)] hover:bg-[var(--bg-base)]'
    );

  return (
    <div>
      {/* Page header */}
      <div className="mb-8">
        <p className="text-[11px] font-semibold uppercase tracking-[0.1em] text-[var(--text-muted)]">
          Operations
        </p>
        <h1 className="font-display text-[32px] font-medium tracking-[-0.02em] text-[var(--text-primary)] mt-1">
          Send notification
        </h1>
        <p className="text-[14px] text-[var(--text-muted)] mt-1">
          Lands in the in-app notifications feed and the device notification tray
          (image expands on Android &amp; iOS).
        </p>
      </div>

      <div className="grid gap-6 items-start xl:grid-cols-[minmax(0,1fr)_400px]">
        {/* ── Composer ─────────────────────────────────────────────────────── */}
        <div className="rounded-3xl bg-white shadow-soft overflow-hidden divide-y divide-[var(--border-subtle)]">
          <ComposerSection
            title="Audience"
            hint="Who receives it — one platform's users, a hand-picked list, or every registered device."
          >
            <div className="flex flex-wrap gap-2">
              <button className={chip(audience === 'app')} onClick={() => setAudience('app')}>By platform</button>
              <button className={chip(audience === 'users')} onClick={() => setAudience('users')}>Specific users</button>
              <button className={chip(audience === 'all')} onClick={() => setAudience('all')}>Everyone</button>
            </div>

            {audience === 'app' && (
              <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-[var(--border-subtle)]">
                {(['customer', 'merchant', 'rider'] as const).map((a) => (
                  <button key={a} className={chip(app === a)} onClick={() => setApp(a)}>
                    <span className="capitalize">{a}s</span>
                  </button>
                ))}
              </div>
            )}

            {audience === 'users' && (
              <div className="mt-3 pt-3 border-t border-[var(--border-subtle)]">
                <input className={inputCls} placeholder="Search name, phone, or email…"
                  value={userQuery} onChange={(e) => setUserQuery(e.target.value)} />
                {matches.length > 0 && (
                  <div className="mt-2 rounded-2xl border border-[var(--border-subtle)] divide-y divide-[var(--border-subtle)] overflow-hidden">
                    {matches.map((u: any) => (
                      <button key={u.userId}
                        className="w-full text-left px-4 py-2.5 text-[13px] text-[var(--text-primary)] hover:bg-[var(--bg-base)] transition-colors duration-150"
                        onClick={() => {
                          setSelectedUsers([...selectedUsers, {
                            userId: u.userId,
                            label: `${u.firstName ?? ''} ${u.lastName ?? ''} · ${u.phone ?? u.email ?? ''}`,
                          }]);
                          setUserQuery('');
                        }}>
                        {u.firstName} {u.lastName} <span className="text-[var(--text-muted)]">· {u.phone ?? u.email}</span>
                      </button>
                    ))}
                  </div>
                )}
                {selectedUsers.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-3">
                    {selectedUsers.map((u) => (
                      <span key={u.userId}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-semibold bg-[var(--bg-elevated)] text-[var(--text-accent)]">
                        {u.label}
                        <button
                          onClick={() => setSelectedUsers(selectedUsers.filter((s) => s.userId !== u.userId))}
                          className="hover:opacity-70 transition-opacity">
                          <X className="w-3 h-3" strokeWidth={2} />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}
          </ComposerSection>

          <ComposerSection
            title="Message"
            hint="Title shows bold in the tray; the body wraps beneath it."
          >
            <div className="space-y-3">
              <div>
                <input className={inputCls} placeholder="Title" maxLength={200}
                  value={title} onChange={(e) => setTitle(e.target.value)} />
                <p className="text-[11px] text-[var(--text-muted)] text-right mt-1">{title.length}/200</p>
              </div>
              <textarea className={cn(inputCls, 'resize-y')} rows={5} placeholder="Body"
                value={body} onChange={(e) => setBody(e.target.value)} />
            </div>
          </ComposerSection>

          <ComposerSection
            title="Attachment"
            hint="Optional image — PNG, JPEG, or WebP. Shows in the feed and expands in the tray."
          >
            <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" hidden
              onChange={(e) => e.target.files?.[0] && pickImage(e.target.files[0])} />
            {imageUrl ? (
              <div className="relative inline-block">
                <img src={imageUrl} alt="attachment"
                  className="h-36 rounded-2xl border border-[var(--border-subtle)] object-cover" />
                <button onClick={() => setImageUrl(null)}
                  className="absolute top-2 right-2 w-7 h-7 rounded-full bg-[var(--brand-forest)]/70 backdrop-blur-sm text-white flex items-center justify-center hover:bg-[var(--brand-forest)] transition-colors duration-200"
                  title="Remove image">
                  <X className="w-3.5 h-3.5" strokeWidth={2} />
                </button>
              </div>
            ) : (
              <button onClick={() => fileRef.current?.click()} disabled={uploading}
                className="w-full flex items-center justify-center gap-2 py-6 rounded-2xl border border-dashed border-[var(--border-default)] text-[13px] font-medium text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:border-[var(--border-strong)] hover:bg-[var(--bg-base)] transition-all duration-200 disabled:opacity-50">
                <ImagePlus className="w-4 h-4" strokeWidth={1.5} />
                {uploading ? 'Uploading…' : 'Attach image (optional)'}
              </button>
            )}
          </ComposerSection>

          <ComposerSection
            title="Delivery"
            hint="Leave blank to send immediately, or pick a future time to schedule."
          >
            <div className="flex flex-wrap items-center gap-3">
              <input type="datetime-local" value={scheduledAt}
                min={new Date(Date.now() + 60_000).toISOString().slice(0, 16)}
                onChange={(e) => setScheduledAt(e.target.value)}
                className="rounded-2xl border border-[var(--border-default)] bg-white px-4 py-2.5 text-[14px] text-[var(--text-primary)] focus:outline-none focus:border-[var(--border-strong)] transition-colors duration-200" />
              {scheduledAt && (
                <>
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-semibold bg-[var(--state-warning-bg)] text-[var(--state-warning)]">
                    <CalendarClock className="w-3.5 h-3.5" strokeWidth={1.5} />
                    Sends {new Date(scheduledAt).toLocaleString()}
                  </span>
                  <button onClick={() => setScheduledAt('')}
                    className="text-[12px] font-semibold text-[var(--text-muted)] hover:text-[var(--text-primary)] underline underline-offset-2 transition-colors duration-200">
                    Clear
                  </button>
                </>
              )}
            </div>
          </ComposerSection>

          {/* Footer action bar */}
          <div className="px-6 py-5 bg-[var(--bg-base)] flex flex-wrap items-center justify-between gap-4">
            <p className="text-[13px] text-[var(--text-secondary)] min-h-[1.25rem]">{result}</p>
            <button onClick={() => send.mutate()} disabled={!canSend || send.isPending}
              className="inline-flex items-center gap-2 rounded-full bg-[var(--brand-forest)] px-6 py-3 text-[14px] font-semibold text-white transition-all duration-300 hover:brightness-110 hover:shadow-[0_12px_32px_rgba(124,179,66,0.45)] active:brightness-95 disabled:opacity-40 disabled:cursor-not-allowed disabled:shadow-none">
              {scheduledAt
                ? <CalendarClock className="w-4 h-4" strokeWidth={1.5} />
                : <Send className="w-4 h-4" strokeWidth={1.5} />}
              {send.isPending
                ? 'Working…'
                : scheduledAt ? 'Schedule notification' : 'Send notification'}
            </button>
          </div>
        </div>

        {/* ── Recent sends rail ────────────────────────────────────────────── */}
        <div className="rounded-3xl bg-white shadow-soft overflow-hidden">
          <div className="px-6 py-5 border-b border-[var(--border-subtle)] flex items-center justify-between">
            <h2 className="text-[15px] font-semibold text-[var(--text-primary)]">Recent sends</h2>
            {history.length > 0 && (
              <span className="text-[12px] font-semibold px-2.5 py-1 rounded-full bg-[var(--bg-base)] text-[var(--text-muted)]">
                {history.length}
              </span>
            )}
          </div>

          {history.length === 0 ? (
            <div className="px-6 py-14 text-center">
              <div className="w-10 h-10 mx-auto rounded-2xl bg-[var(--bg-elevated)] flex items-center justify-center mb-3">
                <Bell className="w-5 h-5 text-[var(--text-accent)]" strokeWidth={1.5} />
              </div>
              <p className="text-[13px] text-[var(--text-muted)]">Nothing sent yet.</p>
            </div>
          ) : (
            <div className="divide-y divide-[var(--border-subtle)] max-h-[680px] overflow-y-auto">
              {history.map((h) => (
                <div key={h.id} className="px-6 py-4 hover:bg-[var(--bg-base)] transition-colors duration-150">
                  <div className="flex items-start gap-3">
                    {h.image_url && (
                      <img src={h.image_url} alt=""
                        className="w-11 h-11 rounded-xl object-cover border border-[var(--border-subtle)] shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-[14px] font-semibold text-[var(--text-primary)] truncate">{h.title}</p>
                        {h.status && h.status !== 'sent' && (
                          <span className={cn(
                            'shrink-0 text-[10px] font-bold uppercase tracking-[0.08em] px-2 py-0.5 rounded-full',
                            statusChip[h.status] ?? 'bg-[var(--bg-base)] text-[var(--text-muted)]'
                          )}>
                            {h.status}
                          </span>
                        )}
                      </div>
                      <p className="text-[12px] text-[var(--text-muted)] line-clamp-2 mt-0.5">{h.body}</p>
                      <p className="text-[11px] text-[var(--text-muted)] mt-1.5">
                        {h.status === 'pending' && h.scheduled_at
                          ? <>Scheduled for {new Date(h.scheduled_at).toLocaleString()} · ~{h.recipient_count} recipients</>
                          : <>
                              {h.recipient_count} recipient{h.recipient_count === 1 ? '' : 's'}
                              {h.delivered_count != null && <> · {h.delivered_count} delivered{(h.failed_count ?? 0) > 0 && <>, {h.failed_count} failed</>}</>}
                              {' · '}{new Date(h.created_at).toLocaleString()}
                            </>}
                      </p>
                      {h.status === 'pending' && (
                        <button
                          onClick={() => cancelMutation.mutate(h.id)}
                          disabled={cancelMutation.isPending}
                          className="mt-2 rounded-full border border-[var(--state-danger-border)] px-3 py-1 text-[11px] font-semibold text-[var(--state-danger)] hover:bg-[var(--state-danger-bg)] transition-all duration-200 disabled:opacity-50">
                          Cancel scheduled send
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
